import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Camera } from "lucide-react";
import { TEAM, PHOTOGRAPHER, API } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { useRevealOnScroll } from "../lib/useReveal";
import { thumb, fallbackToFull } from "../lib/imageThumb";

export default function Professeurs() {
  useSEO({
    title: "Notre équipe — professeurs & photographe partenaire · 1000 ARTS Cugy VD",
    description: "Rencontre l'équipe 1000 ARTS : Noémie, Miller, Roxane, Lydia, Amélie, Camille, Claudio, Carol, Viktor, Léa. Formés en Suisse et à l'international, ils transmettent la danse, le chant et la comédie musicale avec passion. Photos © Suman Photography.",
    canonical: "/professeurs",
  });

  const [openIdx, setOpenIdx] = useState(-1);
  // L'équipe tenue par la console (remplacement d'un prof, 30.10.2026) : les profs arrivés
  // s'ajoutent (photo et texte), ceux qui sont partis disparaissent à leur date. Sans
  // réponse du serveur, la page reste celle d'origine.
  const [equipe, setEquipe] = useState(TEAM);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/equipe`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d) return;
      const partis = new Set(d.retraits || []);
      const prenom = (n) => String(n || "").split(" ")[0].normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      setEquipe([...TEAM.filter((p) => !partis.has(prenom(p.name))),
        ...(d.ajouts || []).map((p) => ({ ...p, image: p.image ? `${API}${p.image.replace(/^\/api/, "")}` : "", placeholder: !p.image }))]);
    }).catch(() => {});
  }, []);
  useRevealOnScroll();

  return (
    <div data-testid="page-professeurs" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <PageDecor />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
      <div className="eyebrow mb-3">L'équipe pédagogique</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight max-w-4xl">
        Notre <span style={{ color: "var(--primary)" }}>équipe</span>
      </h1>
      <p className="mt-6 text-lg md:text-xl text-[var(--muted)] max-w-3xl leading-relaxed">
        Une équipe de passionnés, formés auprès des meilleurs en Suisse et à l'international. Chaque prof continue de se former en permanence pour offrir des cours toujours plus qualitatifs et inspirants.
      </p>

      {/* Grille des profs */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {equipe.map((p, i) => {
          const hasLong = !!p.bio_long;
          const open = openIdx === i;
          return (
            <article
              key={p.name}
              data-testid={`prof-card-${p.name.split(" ")[0].toLowerCase()}`}
              className="rounded-3xl bg-white overflow-hidden flex flex-col hover:shadow-xl transition-all reveal-on-scroll"
              style={{
                borderTop: "5px solid var(--primary)",
                boxShadow: "0 4px 20px rgba(15,27,45,0.06)",
              }}
            >
              <div className="relative w-full overflow-hidden" style={{ aspectRatio: "3/4" }}>
                {p.placeholder ? (
                  <ProfPlaceholder name={p.name} />
                ) : (
                  <img src={thumb(p.image)} onError={fallbackToFull(p.image)} alt={p.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                )}
                {p.placeholder && (
                  <div
                    className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider shadow-sm"
                    style={{ background: "rgba(255,255,255,0.95)", color: "#6B7280" }}
                    data-testid={`prof-placeholder-${p.name.split(" ")[0].toLowerCase()}`}
                  >
                    Photo à venir
                  </div>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="eyebrow text-[10px]" style={{ color: "var(--primary)" }}>{p.role}</div>
                <h3 className="font-display text-2xl mt-2 mb-3">{p.name}</h3>
                <p className="text-sm text-[var(--text)] leading-relaxed">{p.bio}</p>

                {hasLong && (
                  <>
                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? -1 : i)}
                      data-testid={`prof-toggle-${i}`}
                      className="mt-4 inline-flex items-center gap-1 text-sm font-medium self-start hover:underline"
                      style={{ color: "var(--primary)" }}
                      aria-expanded={open}
                    >
                      {open ? "Réduire la bio" : "Lire la bio complète"}
                      <ChevronDown
                        size={14}
                        style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "" }}
                      />
                    </button>
                    {open && (
                      <div
                        data-testid={`prof-biolong-${i}`}
                        className="mt-4 space-y-3 text-sm text-[var(--text)] leading-relaxed border-t border-[var(--border-soft)] pt-4"
                      >
                        {p.bio_long.split("\n\n").map((para, k) => (
                          <p key={k}>{para}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* Photographe partenaire */}
      <section className="mt-24" data-testid="photographer-section">
        <div className="eyebrow mb-3 flex items-center gap-2">
          <Camera size={14} /> Photographe partenaire
        </div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">
          Notre photographe attitré <span style={{ color: "var(--primary)" }}>Suman Warpelin</span>
        </h2>
        <div
          className="mt-8 rounded-3xl overflow-hidden grid md:grid-cols-5 gap-0 shadow-lg"
          style={{ background: "#fff", border: "1px solid var(--border-soft)" }}
          data-testid="photographer-card"
        >
          <div className="md:col-span-2 relative" style={{ minHeight: 340 }}>
            {PHOTOGRAPHER.placeholder ? (
              <ProfPlaceholder name={PHOTOGRAPHER.name} icon="camera" />
            ) : (
              <img
                src={PHOTOGRAPHER.image}
                alt={`${PHOTOGRAPHER.name} — ${PHOTOGRAPHER.brand}`}
                className="w-full h-full object-cover"
                loading="lazy"
                style={{ maxHeight: 480, objectPosition: "50% 15%" }}
              />
            )}
          </div>
          <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
            <div className="eyebrow" style={{ color: "var(--primary)" }}>{PHOTOGRAPHER.role}</div>
            <h3 className="font-display text-3xl md:text-4xl mt-2 mb-4">{PHOTOGRAPHER.name}</h3>
            <div className="text-sm font-semibold uppercase tracking-widest mb-6" style={{ color: "var(--muted)" }}>
              © {PHOTOGRAPHER.brand}
            </div>
            {PHOTOGRAPHER.website && (
              <a
                href={PHOTOGRAPHER.website}
                target="_blank"
                rel="noreferrer"
                data-testid="photographer-website"
                className="inline-flex items-center gap-2 self-start px-6 py-3 rounded-full font-semibold hover:shadow-md hover:scale-[1.02] transition-all"
                style={{ background: "var(--primary)", color: "#fff" }}
              >
                Voir le portfolio <ArrowRight size={16} />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20">
        <div className="card-flat text-center" style={{ padding: "48px 32px" }}>
          <h3 className="font-display text-3xl md:text-4xl">Envie de nous rencontrer&nbsp;?</h3>
          <p className="text-[var(--muted)] mt-3">
            Réserve un cours d'essai — c'est le meilleur moyen de découvrir la vibe 1000 ARTS.
          </p>
          <div className="flex justify-center gap-3 mt-6 flex-wrap">
            <Link to="/ecole" data-testid="prof-cta-ecole" className="btn-outline">L'école</Link>
            <Link to="/inscription" data-testid="prof-cta-inscription" className="btn-primary">
              Réserver un essai <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}

// Placeholder visuel élégant — dégradé turquoise + initiale + icône silhouette/caméra
function ProfPlaceholder({ name, icon = "user" }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className="w-full h-full flex items-center justify-center relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, var(--primary) 0%, #0EA5B7 55%, #22D3EE 100%)",
      }}
      aria-label={`Portrait à venir — ${name}`}
    >
      {/* décor : cercles subtils */}
      <div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full"
        style={{ background: "rgba(255,255,255,0.15)" }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full"
        style={{ background: "rgba(255,255,255,0.08)" }}
        aria-hidden="true"
      />
      {/* Icône stylisée */}
      {icon === "camera" ? (
        <Camera size={64} color="#fff" strokeWidth={1.5} className="opacity-90 relative z-10" />
      ) : (
        <div
          className="font-display text-white flex items-center justify-center relative z-10"
          style={{
            fontSize: "6rem",
            width: "10rem",
            height: "10rem",
            borderRadius: "9999px",
            background: "rgba(255,255,255,0.15)",
            border: "3px solid rgba(255,255,255,0.35)",
            lineHeight: 1,
          }}
        >
          {initial}
        </div>
      )}
    </div>
  );
}
